import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams } from "../contexts";
import { getNumericPhoneNumber, getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    Account,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
    QueryParams,
    QueryResult,
    FeeSummary,
} from "../models";
import { Operation } from "rfc6902";

interface UseAccountQueryParams extends Omit<QueryHookParams, "queryType"> {
    accountTypeId?: string;
    includeDetails?: string;
    phoneNumber?: string;
    ids?: (number | string)[];
    queryType?: QueryType | "billing" | "fees";
    isInternal?: boolean;
}
export interface IUseAccountQuery extends Omit<IUseQueryPaged<Account>, "upsertMutation"> {
    getByPhoneQuery: QueryResult<Account[]>;
    getByIdsQuery: QueryResult<Account[]>;
    billingAccountsQuery: QueryResult<Account[]>;
    feesQuery: QueryResult<FeeSummary[]>;
}

export const useAccountQuery = (params?: UseAccountQueryParams): IUseAccountQuery => {
    const accountId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Accounts);
    const url = Endpoints.Accounts;
    const includeDetailsParam = params?.includeDetails ? "true" : "false";
    const phoneNumber = getNumericPhoneNumber(params?.phoneNumber) ?? "";

    const queryType = useMemo(() => {
        if (params?.queryType === "billing") return "billing";
        if (params?.queryType === "fees") return "fees";
        return getQueryType(accountId, params?.queryType);
    }, [accountId, params?.queryType]);

    const args: QueryParams = {
        accountTypeId: params?.accountTypeId ?? queryParams.accountTypeId,
        includeDetails: includeDetailsParam,
        phoneNumber,
        isInternal: params?.isInternal !== undefined ? params.isInternal ? "true" : "false" : "",
    };

    const queryParamsWithArgs: QueryParams = {
        ...queryParams,
        ...args,
    };

    const billingQueryParams: QueryParams = {
        isBilling: "true",
    };

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Account>>(getRequestOptions(url, config, queryParamsWithArgs, sortParams, pageParams)),
        queryKey: queryKeys.accounts.pagedList({ ...queryParamsWithArgs, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList && !params?.ids,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Account>>(getRequestOptions(url, config, args, sortParams, undefined, false, true)).then(
                (response) => response.items
            ),
        queryKey: queryKeys.accounts.list({ ...args, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const billingAccountsQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Account>>(
                getRequestOptions(url, config, billingQueryParams, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.accounts.list({ ...billingQueryParams, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === "billing",
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Account>({ url: `${url}/${accountId}`, config }),
        queryKey: queryKeys.accounts.detail(accountId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    const getByPhoneQuery = useGenericQuery({
        queryFn: (config) =>
            get<Account[]>({
                url: `${url}/phone?phoneNumber=${phoneNumber}&accountTypeId=${params?.accountTypeId}`,
                config,
            }),
        queryKey: queryKeys.accounts.getByPhone(phoneNumber),
        placeholderData: undefined,
        enabled: isAuthorized && !!phoneNumber && !!params?.accountTypeId,
        cacheTime: 2000,
        staleTime: 2000,
    });

    const getByIdsQuery = useGenericQuery({
        queryFn: (config) => get<Account[]>({ url: `${url}/basic?ids=${params?.ids?.join(",")}`, config }),
        queryKey: queryKeys.accounts.getByIds(params?.ids),
        placeholderData: [],
        enabled: isAuthorized && !!params?.ids?.length,
    });

    const feesQuery = useGenericQuery({
        queryFn: (config) => get<FeeSummary[]>({ url: `${url}/${accountId}/fees`, config }),
        queryKey: queryKeys.accounts.fees(accountId),
        placeholderData: [],
        enabled: isAuthorized && queryType === "fees",
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<Account>) => post<Partial<Account>, Account>({ url, data }),
        queryKey: queryKeys.accounts.all(),
        keysToInvalidate: [queryKeys.accounts.lists(), queryKeys.accounts.pagedLists()],
        successToast: "Account created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Account>({ url: `${url}/${accountId}`, data }),
        queryKey: queryKeys.accounts.detail(accountId),
        keysToInvalidate: [queryKeys.accounts.lists(), queryKeys.accounts.pagedLists()],
        successToast: "Account updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${accountId}` }),
        queryKey: queryKeys.accounts.detail(accountId),
        keysToInvalidate: [queryKeys.accounts.lists(), queryKeys.accounts.pagedLists()],
        successToast: "Account deleted",
    });

    const uploadMutation = useGenericMutation({
        mutationFn: (data: FormData) => post<FormData, Account[]>({ url: `${url}/upload`, data }),
        queryKey: queryKeys.formSelects.all(),
        keysToInvalidate: [queryKeys.accounts.all(), queryKeys.accounts.lists(), queryKeys.accounts.pagedLists()],
        successToast: "Accounts created",
    });

    return useMemo<IUseAccountQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            billingAccountsQuery,
            createMutation,
            updateMutation,
            deleteMutation,
            uploadMutation,
            getByPhoneQuery,
            getByIdsQuery,
            feesQuery,
        }),
        [
            pagedListQuery,
            listQuery,
            detailQuery,
            billingAccountsQuery,
            createMutation,
            updateMutation,
            deleteMutation,
            uploadMutation,
            getByPhoneQuery,
            getByIdsQuery,
            feesQuery,
        ]
    );
};
